import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineGrid,
  Box,
  Button,
  Spinner,
  Banner,
  Pagination,
  Select,
} from '@shopify/polaris';

interface Product {
  id: string;
  name: string;
  slug: string;
  sku: string;
  primaryImage: string | null;
  startingPrice: number | null;
  samplePrice: number | null;
  isHotSelling: boolean;
  stockStatus: 'IN_STOCK' | 'OUT_OF_STOCK';
  categoryName: string;
}

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  bannerUrl: string | null;
  productCount: number;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

export function CategoryPage() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const [category, setCategory] = useState<Category | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('newest');
  const [limit] = useState(12);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    async function fetchData() {
      if (!slug) return;

      setLoading(true);
      setError(null);

      try {
        // Fetch category info
        const catRes = await fetch(`${API_URL}/categories`, { signal });
        if (!catRes.ok) throw new Error('Failed to fetch category');
        const catData = await catRes.json();
        const foundCategory = catData.data?.find((c: Category) => c.slug === slug);

        if (!foundCategory) {
          setError('Category not found');
          setLoading(false);
          return;
        }

        setCategory(foundCategory);

        // Fetch products
        const prodRes = await fetch(
          `${API_URL}/products?categorySlug=${slug}&page=${page}&limit=${limit}&sortBy=${sortBy}`,
          { signal }
        );
        if (!prodRes.ok) throw new Error('Failed to fetch products');
        const prodData = await prodRes.json();

        setProducts(prodData.data || []);
        setTotalPages(prodData.meta?.total ? Math.ceil(prodData.meta.total / limit) : 1);
      } catch (err) {
        // Ignore abort errors
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        setError('Failed to load data.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();

    return () => {
      controller.abort();
    };
  }, [slug, page, sortBy, limit]);

  if (loading) {
    return (
      <Page title="Loading...">
        <Box padding="800">
          <BlockStack align="center">
            <Spinner size="large" />
            <Text as="p">Loading products...</Text>
          </BlockStack>
        </Box>
      </Page>
    );
  }

  if (error || !category) {
    return (
      <Page title="Error">
        <Banner tone="critical">{error || 'Category not found'}</Banner>
        <Box padding="400">
          <Button onClick={() => navigate('/categories')}>Back to Categories</Button>
        </Box>
      </Page>
    );
  }

  const sortOptions = [
    { label: 'Newest', value: 'newest' },
    { label: 'Price: Low to High', value: 'price_asc' },
    { label: 'Price: High to Low', value: 'price_desc' },
    { label: 'Name', value: 'name' },
  ];

  return (
    <Page
      title={category.name}
      subtitle={`${category.productCount} products`}
      backAction={{ content: 'Categories', onAction: () => navigate('/categories') }}
    >
      <Layout>
        {/* Category Banner */}
        {category.bannerUrl && (
          <Layout.Section>
            <Card>
              <div style={{ borderRadius: '8px', overflow: 'hidden' }}>
                <img
                  src={category.bannerUrl}
                  alt={category.name}
                  loading="lazy"
                  width={800}
                  height={200}
                  style={{
                    width: '100%',
                    height: '200px',
                    objectFit: 'cover',
                  }}
                />
              </div>
              {category.description && (
                <Box padding="400">
                  <Text as="p" variant="bodyMd">
                    {category.description}
                  </Text>
                </Box>
              )}
            </Card>
          </Layout.Section>
        )}

        {/* Filters */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <Box maxWidth="200px">
                <Select
                  label="Sort by"
                  options={sortOptions}
                  value={sortBy}
                  onChange={(value) => {
                    setSortBy(value);
                    setPage(1);
                  }}
                />
              </Box>
            </Box>
          </Card>
        </Layout.Section>

        {/* Products Grid */}
        <Layout.Section>
          {products.length === 0 ? (
            <Card>
              <Box padding="800">
                <Text as="p" alignment="center">
                  No products found in this category.
                </Text>
              </Box>
            </Card>
          ) : (
            <InlineGrid columns={{ xs: 1, sm: 2, md: 3, lg: 4 }} gap="400">
              {products.map((product) => (
                <Card key={product.id}>
                  <BlockStack gap="200">
                    <Box
                      minHeight="180px"
                      background="bg-surface-secondary"
                      borderRadius="200"
                      position="relative"
                    >
                      {product.primaryImage ? (
                        <img
                          src={product.primaryImage}
                          alt={product.name}
                          loading="lazy"
                          width={200}
                          height={180}
                          style={{
                            width: '100%',
                            height: '180px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                          }}
                        />
                      ) : (
                        <Box
                          minHeight="180px"
                          background="bg-surface-tertiary"
                          borderRadius="200"
                        />
                      )}
                      {product.isHotSelling && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            padding: '4px 8px',
                            backgroundColor: 'var(--p-color-bg-fill-success)',
                            borderRadius: '4px',
                          }}
                        >
                          <Text as="span" variant="bodySm" fontWeight="bold">
                            Hot
                          </Text>
                        </div>
                      )}
                      {/* Out-of-stock items stay visible but clearly marked. */}
                      {product.stockStatus === 'OUT_OF_STOCK' && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '8px',
                            left: '8px',
                            padding: '4px 8px',
                            backgroundColor: 'var(--p-color-bg-fill-caution)',
                            borderRadius: '4px',
                          }}
                        >
                          <Text as="span" variant="bodySm" fontWeight="bold">
                            Out of Stock
                          </Text>
                        </div>
                      )}
                    </Box>
                    <Box padding="200">
                    <BlockStack gap="100">
                      <Text as="h3" variant="bodyMd" fontWeight="semibold">
                        {product.name}
                      </Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        SKU: {product.sku}
                      </Text>
                      {(product.startingPrice || product.samplePrice) && (
                        <Text as="p" variant="bodyMd" fontWeight="bold">
                          Starting at Rs.{product.startingPrice || product.samplePrice}
                        </Text>
                      )}
                      <Button
                        fullWidth
                        onClick={() => navigate(`/products/${product.slug}`)}
                      >
                        View Details
                      </Button>
                    </BlockStack>
                    </Box>
                  </BlockStack>
                </Card>
              ))}
            </InlineGrid>
          )}
        </Layout.Section>

        {/* Pagination */}
        {totalPages > 1 && (
          <Layout.Section>
            <Box padding="400">
              <BlockStack align="center">
                <Pagination
                  hasPrevious={page > 1}
                  hasNext={page < totalPages}
                  onPrevious={() => setPage(page - 1)}
                  onNext={() => setPage(page + 1)}
                />
              </BlockStack>
            </Box>
          </Layout.Section>
        )}
      </Layout>
    </Page>
  );
}
